import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '../../store';
import { AnnotatedError, errorsToUserCopy } from '../../text/errorsToUserCopy';
import { selectInstrumentDataById } from '../workflow/selectors';

import { WorkflowSerializationError, selectWorkflowJsonToPreview } from '.';

/** All workflow-level errors */
export const selectWorkflowLevelErrors = createSelector(
  selectWorkflowJsonToPreview,
  selectInstrumentDataById,
  (
    maybeWorkflow,
    instrumentsById
  ): AnnotatedError<WorkflowSerializationError>[] => {
    const errors = maybeWorkflow.isError ? maybeWorkflow.errors : [];
    return errorsToUserCopy(errors, instrumentsById);
  }
);

/** Given a labwareId, get all relevant workflow-level errors (any error that carries this labwareId) */
export const selectWorkflowLevelErrorsForLabwareId = createSelector(
  selectWorkflowLevelErrors,
  (_: RootState, labwareId: string) => labwareId,
  (errors, labwareId): AnnotatedError<WorkflowSerializationError>[] => {
    const result: AnnotatedError<WorkflowSerializationError>[] = [];
    for (const { error, ...annotations } of errors) {
      if ('labwareId' in error && error.labwareId === labwareId) {
        result.push({ error, ...annotations });
      }
    }
    return result;
  }
);

/** Given a taskId, get all relevant workflow-level task errors */
export const selectWorkflowLevelErrorsForTaskId = createSelector(
  selectWorkflowLevelErrors,
  (_: RootState, taskId: string) => taskId,
  (errors, taskId): AnnotatedError<WorkflowSerializationError>[] =>
    errors.filter(({ error }) => 'taskId' in error && error.taskId === taskId)
);
